import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Section, EmptyState } from "@/components/app/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const STRUCTURE_LABEL = {
  blanket: "Blanket (lump-sum)",
  campaign_lined: "Campaign-lined",
  influencer_lined: "Influencer-lined",
};

const _k = () => (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `r_${Math.random().toString(36).slice(2, 8)}`;

function submitLabel(busy, mode) {
  if (busy) return "Saving…";
  return mode === "email" ? "File Email Intake" : "Record PO";
}

function utilisationTone(pct) {
  if (pct >= 95) return "bg-rose-500";
  if (pct >= 75) return "bg-amber-500";
  return "bg-emerald-500";
}

function IntakeDialog({ tenants, onCreated }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("form");        // form | email
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    tenant_id: "unilever", po_number: "", po_date: new Date().toISOString().slice(0, 10),
    total_amount: 0, currency: "INR", structure: "blanket",
    email_from: "", email_subject: "", email_body: "",
    lines: [{ _k: _k(), description: "", amount: 0 }],
  });

  const addLine = () => setF({ ...f, lines: [...f.lines, { _k: _k(), description: "", amount: 0 }] });
  const rmLine = (i) => setF({ ...f, lines: f.lines.filter((_, idx) => idx !== i) });
  const patchLine = (i, k, v) => setF({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) });

  const submitForm = async () => {
    if (!f.po_number.trim()) return toast.error("PO number required");
    if (Number(f.total_amount) <= 0) return toast.error("Total amount must be > 0");
    setBusy(true);
    try {
      const payload = {
        tenant_id: f.tenant_id, po_number: f.po_number.trim(), po_date: f.po_date,
        total_amount: Number(f.total_amount), currency: f.currency, structure: f.structure,
        lines: f.lines
          .filter((l) => Number(l.amount) > 0)
          .map(({ _k: _, ...l }) => ({ ...l, amount: Number(l.amount) })),
      };
      const r = await api.post("/aggregate-pos", payload);
      toast.success(`Aggregate PO ${r.data.po_number} recorded`);
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = async () => {
    if (!f.email_subject.trim() || !f.po_number.trim()) return toast.error("Email subject and PO number required");
    setBusy(true);
    try {
      await api.post("/aggregate-pos/email-intake", {
        tenant_id: f.tenant_id, email_from: f.email_from, email_subject: f.email_subject,
        email_body: f.email_body, po_number: f.po_number.trim(), po_date: f.po_date,
        total_amount: Number(f.total_amount), currency: f.currency, structure: f.structure,
      });
      toast.success(`Email intake filed as PO ${f.po_number}`);
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Intake failed");
    } finally {
      setBusy(false);
    }
  };

  const lineSum = f.lines.reduce((a, l) => a + (Number(l.amount) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-aggregate-po-btn" className="bg-[#0052FF] hover:bg-[#0043D9]">+ Record Aggregate PO</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Record Unilever Aggregate PO (Coupa)</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2 text-xs uppercase tracking-widest font-bold border-b mb-3">
          <button type="button" className={`pb-2 px-1 ${mode === "form" ? "text-[#0052FF] border-b-2 border-[#0052FF]" : "text-slate-400"}`} onClick={() => setMode("form")}>Manual form</button>
          <button type="button" className={`pb-2 px-1 ${mode === "email" ? "text-[#0052FF] border-b-2 border-[#0052FF]" : "text-slate-400"}`} onClick={() => setMode("email")} data-testid="email-intake-tab">Paste email</button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500">FMCG tenant *</label>
            <select className="w-full h-10 rounded-md border border-input px-3 text-sm bg-white" value={f.tenant_id} onChange={(e) => setF({ ...f, tenant_id: e.target.value })}>
              {tenants.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">PO structure *</label>
            <select className="w-full h-10 rounded-md border border-input px-3 text-sm bg-white" value={f.structure} onChange={(e) => setF({ ...f, structure: e.target.value })} data-testid="po-structure">
              <option value="blanket">Blanket (lump-sum)</option>
              <option value="campaign_lined">Campaign-lined</option>
              <option value="influencer_lined">Influencer-lined</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">PO number *</label>
            <Input value={f.po_number} onChange={(e) => setF({ ...f, po_number: e.target.value })} placeholder="UNIL-BLANKET-2026-Q1" data-testid="po-number-input" />
          </div>
          <div>
            <label className="text-xs text-slate-500">PO date *</label>
            <Input type="date" value={f.po_date} onChange={(e) => setF({ ...f, po_date: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-slate-500">Total amount *</label>
            <Input type="number" value={f.total_amount} onChange={(e) => setF({ ...f, total_amount: e.target.value })} data-testid="po-total-input" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Currency</label>
            <Input value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} />
          </div>
        </div>

        {mode === "email" && (
          <div className="space-y-3 pt-3 border-t mt-3">
            <div>
              <label className="text-xs text-slate-500">From (Unilever sender)</label>
              <Input value={f.email_from} onChange={(e) => setF({ ...f, email_from: e.target.value })} placeholder="coupa@unilever.example" data-testid="email-from" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Subject *</label>
              <Input value={f.email_subject} onChange={(e) => setF({ ...f, email_subject: e.target.value })} placeholder="PO UNIL-2026-9987 issued to PROPAY GLOBAL PVT LTD" data-testid="email-subject" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Email body</label>
              <Textarea rows={5} value={f.email_body} onChange={(e) => setF({ ...f, email_body: e.target.value })} placeholder="Paste the full email body here…" data-testid="email-body" />
            </div>
          </div>
        )}

        {mode === "form" && f.structure !== "blanket" && (
          <div className="pt-3 border-t mt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">PO Lines</div>
              <Button type="button" size="sm" variant="outline" onClick={addLine}>+ Add line</Button>
            </div>
            {f.lines.map((l, i) => (
              <div key={l._k} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`po-line-${i}`}>
                <Input className="col-span-8" placeholder="Line description (e.g. Diwali 2026 Bru Coffee campaign)" value={l.description} onChange={(e) => patchLine(i, "description", e.target.value)} />
                <Input className="col-span-3" type="number" placeholder="Amount" value={l.amount} onChange={(e) => patchLine(i, "amount", parseFloat(e.target.value) || 0)} />
                {f.lines.length > 1 && <button type="button" onClick={() => rmLine(i)} className="col-span-1 text-rose-600 text-xs">✕</button>}
              </div>
            ))}
            <div className="text-xs text-slate-500 pt-1 border-t flex justify-between">
              <span>Sum of lines</span>
              <span className={`font-mono ${Math.abs(lineSum - Number(f.total_amount)) > 1 ? "text-rose-600 font-semibold" : "text-emerald-700 font-semibold"}`}>
                {f.currency} {lineSum.toLocaleString()} {Math.abs(lineSum - Number(f.total_amount)) > 1 && `(≠ total)`}
              </span>
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-3">
          <Button variant="ghost" onClick={() => setOpen(false)} className="flex-1">Cancel</Button>
          <Button onClick={mode === "email" ? submitEmail : submitForm} disabled={busy} className="flex-1 bg-[#0052FF] hover:bg-[#0043D9]" data-testid="submit-aggregate-po-btn">
            {submitLabel(busy, mode)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LineMapper({ apId, line, campaigns, orders, onMapped }) {
  const [busy, setBusy] = useState(false);
  const [campaignId, setCampaignId] = useState(line.campaign_id || "");
  const [orderId, setOrderId] = useState(line.order_id || "");
  const campaignOrders = useMemo(() => orders.filter((o) => !campaignId || o.campaign_id === campaignId), [orders, campaignId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/aggregate-pos/${apId}/map-line`, { line_id: line.id, campaign_id: campaignId || null, order_id: orderId || null });
      toast.success("Line mapped");
      onMapped();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Map failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid={`line-mapper-${line.id}`}>
      <select className="h-8 rounded border border-input px-2 text-xs bg-white flex-1" value={campaignId} onChange={(e) => { setCampaignId(e.target.value); setOrderId(""); }}>
        <option value="">— Campaign —</option>
        {campaigns.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
      </select>
      <select className="h-8 rounded border border-input px-2 text-xs bg-white flex-1" value={orderId} onChange={(e) => setOrderId(e.target.value)}>
        <option value="">— Or a specific influencer order —</option>
        {campaignOrders.map((o) => (
          <option key={o.id} value={o.id}>{o.order_number} · {o.influencer_name}</option>
        ))}
      </select>
      <Button size="sm" onClick={save} disabled={busy} className="bg-[#0052FF] hover:bg-[#0043D9] text-xs h-8" data-testid={`map-line-btn-${line.id}`}>{busy ? "…" : "Map"}</Button>
    </div>
  );
}

export default function AggregatePOs() {
  const { user } = useAuth();
  const { id } = useParams();
  const [tenants, setTenants] = useState([]);
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [consumption, setConsumption] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [orders, setOrders] = useState([]);

  const load = useCallback(() => {
    api.get("/tenants").then((r) => setTenants((r.data || []).filter((t) => !t.is_propay && !t.is_network && !t.is_audit_partner))).catch(() => {});
    if (id) {
      api.get(`/aggregate-pos/${id}`).then((r) => setDetail(r.data)).catch(() => {});
      api.get(`/aggregate-pos/${id}/consumption`).then((r) => setConsumption(r.data)).catch(() => {});
      api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    } else {
      api.get("/aggregate-pos").then((r) => setList(r.data)).catch(() => {});
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const home = user?.role === "propay_admin" ? "/admin" : "/procurement";

  if (id && detail) {
    return (
      <div className="space-y-6" data-testid="aggregate-po-detail">
        <div className="text-sm text-slate-500">
          <Link className="hover:underline" to={`${home}/aggregate-pos`}>← Aggregate POs</Link>
        </div>
        <Section title={`Aggregate PO ${detail.po_number}`} subtitle={`${detail.tenant_name} · ${STRUCTURE_LABEL[detail.structure]} · Total ${detail.currency} ${Number(detail.total_amount).toLocaleString()}`} />
        <div className="grid grid-cols-3 gap-3">
          <Card><CardHeader className="pb-1"><CardTitle className="text-xs uppercase tracking-widest text-slate-500">PO Total</CardTitle></CardHeader><CardContent className="text-2xl font-heading font-black">{detail.currency} {Number(detail.total_amount).toLocaleString()}</CardContent></Card>
          <Card><CardHeader className="pb-1"><CardTitle className="text-xs uppercase tracking-widest text-emerald-700">Mapped</CardTitle></CardHeader><CardContent className="text-2xl font-heading font-black text-emerald-700">{detail.currency} {Number(detail.mapped_amount).toLocaleString()}</CardContent></Card>
          <Card><CardHeader className="pb-1"><CardTitle className="text-xs uppercase tracking-widest text-rose-700">Unmapped</CardTitle></CardHeader><CardContent className="text-2xl font-heading font-black text-rose-700">{detail.currency} {Number(detail.unmapped_amount).toLocaleString()}</CardContent></Card>
        </div>

        {/* Blanket PO Consumption Tracker */}
        {consumption && (
          <div className="rounded-xl border-2 border-[#0052FF]/30 bg-gradient-to-br from-[#0052FF]/5 to-white p-4 space-y-2" data-testid="po-consumption-tracker">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-[#0052FF]">PO Consumption</div>
                <div className="text-xs text-slate-500 mt-0.5">Back-to-back POs already issued against this PO envelope.</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-heading font-black text-slate-900">{consumption.utilisation_pct}%</div>
                <div className="text-[10px] text-slate-500">utilised</div>
              </div>
            </div>
            <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`h-full transition-all ${utilisationTone(consumption.utilisation_pct)}`}
                style={{ width: `${Math.min(100, consumption.utilisation_pct)}%` }}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between text-xs text-slate-600 pt-1">
              <span>Consumed <b className="font-mono">{detail.currency} {Number(consumption.consumed_amount).toLocaleString()}</b> · {consumption.b2b_po_count} back-to-back PO(s)</span>
              <span>Available <b className="font-mono">{detail.currency} {Number(consumption.available_amount).toLocaleString()}</b></span>
            </div>
            {consumption.utilisation_pct >= 90 && (
              <div className="text-[11px] text-rose-700 font-semibold pt-1">⚠ Nearing PO envelope — request a top-up before issuing further influencer POs.</div>
            )}
          </div>
        )}
        {detail.email_subject && (
          <div className="rounded-lg border bg-slate-50 p-3 text-sm">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Email intake</div>
            <div className="text-slate-700 font-semibold">{detail.email_subject}</div>
            {detail.email_body && <div className="text-xs text-slate-600 whitespace-pre-line mt-2">{detail.email_body}</div>}
          </div>
        )}
        <Section title="PO Lines" subtitle="Map each line to a Campaign (roll-up) or a specific Influencer Order.">
          {detail.lines?.length ? (
            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Description</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3 text-left">Mapping</th>
                    <th className="px-4 py-3 text-left">Assign to</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.lines.map((l) => (
                    <tr key={l.id} className="border-t" data-testid={`ap-line-row-${l.id}`}>
                      <td className="px-4 py-3">{l.description}</td>
                      <td className="px-4 py-3 text-right font-mono">{detail.currency} {Number(l.amount).toLocaleString()}</td>
                      <td className="px-4 py-3 text-xs">
                        {l.campaign_name && <Badge variant="outline" className="mr-1">Campaign · {l.campaign_name}</Badge>}
                        {l.order_number && <Badge variant="outline">Order · {l.order_number}</Badge>}
                        {!l.campaign_id && !l.order_id && <span className="text-rose-600 font-semibold">Unmapped</span>}
                      </td>
                      <td className="px-4 py-3">
                        <LineMapper apId={detail.id} line={l} campaigns={campaigns} orders={orders} onMapped={load} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rounded-lg border bg-slate-50 p-6 text-sm text-slate-500 text-center">
              This PO is a blanket lump-sum with no explicit lines. Blanket POs allocate directly to campaigns as they are consumed.
            </div>
          )}
        </Section>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="aggregate-pos-list">
      <Section
        title="Aggregate POs (Coupa)"
        subtitle="Blanket / campaign-lined / influencer-lined POs received from FMCG customers via Coupa email."
        right={<IntakeDialog tenants={tenants} onCreated={load} />}
      />
      {list.length === 0 ? (
        <EmptyState title="No aggregate POs yet" hint="Record the first Unilever aggregate PO — it can be a blanket lump-sum, campaign-lined, or influencer-lined." />
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">PO #</th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Structure</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-right">Open</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`ap-row-${p.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{p.po_number}</td>
                  <td className="px-4 py-3">{p.tenant_name}</td>
                  <td className="px-4 py-3"><Badge variant="outline">{STRUCTURE_LABEL[p.structure]}</Badge></td>
                  <td className="px-4 py-3 text-right font-semibold">{p.currency} {Number(p.total_amount).toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{p.po_date}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`${home}/aggregate-pos/${p.id}`}><Button size="sm" variant="outline">Open</Button></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
